import React, {
  FC,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Skeleton from "@mui/material/Skeleton";
import Link from "@mui/material/Link";
import { GridRow, GridRowProps, type GridColumnGroup } from "@mui/x-data-grid";
import {
  useGetCompanyListBasicQuery,
  SortDir,
  ModelScoreSortColumn,
} from "@/generated/company-search";
import { formatNumber } from "@/utils/numberUtils";
import { CompanyDrawer } from "@/pages/Company/CompanyDrawer";
import { COMPANY_SEARCH_CLIENT } from "@/utils/consts";
import { DataTable, useDataTableState } from "@/components/ui/DataTable";
import { DataRow } from "./types";
import {
  getBaseColumns,
  getScoringColumns,
  getModelScoringColumns,
  type OnScoreClick,
  type OnModelScoreClick,
} from "./columns";
import { parseModelSortField } from "@/lib/scoring/modelScoreFields";
import { ScoreDrawer } from "@/lib/scoring/components/ScoreDrawer";
import { ModelScoreDrawer } from "@/lib/scoring/components/ModelScoreDrawer";
import { useModelScoreBreakdown } from "@/apis/companySearch/aiScoring";
import type { ScoringModel } from "@/apis/argo/scoringModels";
import { openAiScoringModel } from "@/utils/module-urls";
import { useScoringStore } from "@/store/scoringStore";
import {
  transformFilterScoring,
  getRawProfileScores,
  buildModelScores,
} from "@/lib/scoring/scoringUtils";
import type { ScoringProfile } from "@/lib/scoring/types";
import { getTenantId } from "@/utils/auth";
import {
  CompaniesEmptyState,
  CompaniesErrorState,
} from "./CompaniesEmptyState";
import { FIT_CRITERION_KEYS } from "@/lib/scoring/criterionRegistry";
import { useFeatureFlag } from "@/hooks/useLaunchDarkly";
import { stripIntentUiFields } from "@/lib/company-filter/utils/stripIntentUiFields";
import {
  buildModelScoreFilter,
  withModelScoreFilterModels,
} from "@/lib/company-filter/utils/buildModelScoreFilter";
import type { CompanyFilters } from "@/lib/company-filter/types";
import {
  toCompanyProfileInstallFilters,
  toCompanyProfileIntentFilters,
} from "@/lib/company-filter";
import { useLocation, useParams } from "react-router-dom";
import {
  buildCompanyRouteContextSearch,
  getExploreSearchFromSearch,
} from "@/utils/companyRouteContext";

/**
 * Custom row component that renders as an anchor tag for SEO/accessibility.
 * Clicking is handled by DataTable's onRowClick, not the link.
 */
const LinkRow = React.forwardRef<HTMLAnchorElement, GridRowProps>(
  (props, ref) => {
    const { row } = props;
    const location = useLocation();
    const { segmentId } = useParams<{ segmentId?: string }>();
    const search = getExploreSearchFromSearch(location.search);
    const routeContext = buildCompanyRouteContextSearch({ segmentId, search });
    const href = `/catalog/companies/${row?.hgId}${routeContext}`;

    return (
      <Link
        ref={ref}
        href={href}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        underline="none"
      >
        <GridRow {...props} />
      </Link>
    );
  }
);

LinkRow.displayName = "LinkRow";

interface CompaniesExploreListProps {
  /** Filters to apply to the GraphQL query */
  filters?: CompanyFilters;
  /** Ephemeral search term applied to company search (not persisted in segments). */
  fuzzySearch?: string;
  /** Whether to localize installs to company country */
  localizeInstalls?: boolean;
  /** Passes the latest server-reported result count (when available). */
  onCountChange?: (count: number | undefined) => void;
  /** Called when user clicks "CHANGE SCORING" in the score drawer */
  onEditProfile?: (profile: ScoringProfile) => void;
  /** Notified whenever the active sort changes, with the backend-format field name (e.g. "Fit Score Normalized-1") and direction ("asc"/"desc"). */
  onActiveSortChange?: (field: string, direction: string) => void;
  /** Ephemeral fit/intent score band applied from a matrix cell drill-down (not persisted). */
  drillBand?: {
    fit?: { min?: number; max?: number };
    intent?: { min?: number; max?: number };
  } | null;
}

const DEFAULT_SORT_FIELD = "revenue_range_min";

export const CompaniesExploreList: FC<CompaniesExploreListProps> = ({
  filters,
  fuzzySearch,
  localizeInstalls = true,
  onCountChange,
  onEditProfile,
  onActiveSortChange,
  drillBand,
}) => {
  const trimmedFuzzySearch = fuzzySearch?.trim() || "";
  const apiFilters = useMemo(() => stripIntentUiFields(filters), [filters]);

  // Scoring store — active profiles drive score columns
  const profiles = useScoringStore((s) => s.profiles);
  const activeProfileIds = useScoringStore((s) => s.activeProfileIds);
  const activeProfiles = useMemo(
    () =>
      activeProfileIds
        .map((id) => profiles.find((p) => p.id === id))
        .filter((p): p is ScoringProfile => p !== undefined),
    [profiles, activeProfileIds]
  );

  // Build scoring filter and merge with existing filters
  const scoringInput = useMemo(
    () => transformFilterScoring(activeProfiles),
    [activeProfiles]
  );

  // V2 (argo model) scoring — gated behind a flag; additive to V1 above.
  const scoringV2Enabled = useFeatureFlag("maScoringModelsV2", false);
  const models = useScoringStore((s) => s.models);
  const activeModelFamilies = useScoringStore((s) => s.activeModelFamilies);
  const fetchModels = useScoringStore((s) => s.fetchModels);
  const tenantId = useMemo(() => getTenantId(), []);

  useEffect(() => {
    if (scoringV2Enabled && tenantId) void fetchModels(tenantId);
  }, [scoringV2Enabled, tenantId, fetchModels]);

  const activeModels = useMemo(
    () =>
      scoringV2Enabled
        ? activeModelFamilies
            .map((family) => models.find((m) => m.family === family))
            .filter((m): m is NonNullable<typeof m> => m !== undefined)
        : [],
    [scoringV2Enabled, models, activeModelFamilies]
  );

  // The query carries the displayed models plus any model a stored filter
  // targets — removing a model from view must not unfilter the list
  // (RGI-2347). Columns/sort still key off `activeModels`, so the extra
  // models add no UI.
  const queryModels = useMemo(
    () =>
      withModelScoreFilterModels(
        scoringV2Enabled,
        activeModels,
        models,
        filters
      ),
    [scoringV2Enabled, activeModels, models, filters]
  );

  const modelScores = useMemo(
    () => buildModelScores(queryModels),
    [queryModels]
  );

  // V2 model-score filter: hard per-model filters on composite range and/or
  // tier set. Shared with the save-as-workbook cap gate in CompaniesExplorePage
  // so both count the same set.
  const modelScoreFilter = useMemo(
    () => buildModelScoreFilter(scoringV2Enabled, queryModels, filters),
    [scoringV2Enabled, queryModels, filters]
  );
  const queryFilters = useMemo(() => {
    if (!trimmedFuzzySearch) return apiFilters;
    return { ...(apiFilters ?? {}), fuzzySearch: trimmedFuzzySearch };
  }, [apiFilters, trimmedFuzzySearch]);

  // A matrix cell drill-down overlays an ephemeral fit+intent band on the query
  // (not persisted to the segment).
  const effectiveFilters = useMemo(() => {
    if (!drillBand) return queryFilters;
    return {
      ...(queryFilters ?? {}),
      ...(drillBand.fit ? { fitScore: drillBand.fit } : {}),
      ...(drillBand.intent ? { intentScore: drillBand.intent } : {}),
    };
  }, [queryFilters, drillBand]);

  // Table state managed by hook
  const tableState = useDataTableState({
    initialPageSize: 50,
    initialSort: [{ field: DEFAULT_SORT_FIELD, sort: "desc" }],
    resetPageOnSort: true,
  });
  const { setPage, onSortModelChange } = tableState;

  // Reset to first page whenever filters change
  useEffect(() => {
    setPage(0);
  }, [filters, trimmedFuzzySearch, localizeInstalls, drillBand, setPage]);

  // Auto-sort by scores when profiles change.
  // currentSortField is intentionally omitted from deps — we only want this to
  // fire on profile changes, not on every sort click (which would override the user's choice).
  const currentSortField = tableState.sortModel[0]?.field ?? "";
  useEffect(() => {
    const isScoreSort = currentSortField.startsWith("score_");
    const sortedId = isScoreSort
      ? parseInt(currentSortField.split("_")[1], 10)
      : null;
    const sortedProfileActive =
      sortedId !== null && activeProfiles.some((p) => p.id === sortedId);

    if (activeProfiles.length === 0) {
      if (isScoreSort)
        onSortModelChange([{ field: DEFAULT_SORT_FIELD, sort: "desc" }]);
    } else if (!sortedProfileActive) {
      const [firstProfile] = activeProfiles;
      const hasFit = firstProfile.parameters.criteria.some(
        (c) =>
          c !== null &&
          typeof c === "object" &&
          FIT_CRITERION_KEYS.some((k) => k in (c as object))
      );
      const scoreType = hasFit ? "fit" : "intent";
      onSortModelChange([
        { field: `score_${firstProfile.id}_${scoreType}`, sort: "desc" },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfileIds.join(","), onSortModelChange]);

  // Same for V2 models: when the model whose Total/Fit/Need/Intent column is
  // the active sort is removed, fall back to the default sort so the stale
  // `mscore_*` field is never forwarded as a generic `sort` field.
  useEffect(() => {
    const parsed = parseModelSortField(currentSortField);
    if (parsed && !activeModelFamilies.includes(parsed.family))
      onSortModelChange([{ field: DEFAULT_SORT_FIELD, sort: "desc" }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModelFamilies.join(","), onSortModelChange]);

  // Drawer state for company details
  const [drawerState, setDrawerState] = useState<{
    open: boolean;
    companyId: string | null;
  }>({ open: false, companyId: null });

  // Score breakdown drawer state
  const [scoreDrawerState, setScoreDrawerState] = useState<{
    open: boolean;
    company: DataRow | null;
    clickedProfileId: number | null;
    scoreType: "fit" | "intent";
  }>({
    open: false,
    company: null,
    clickedProfileId: null,
    scoreType: "fit",
  });

  const scoreClickedRef = useRef(false);

  const handleScoreClick: OnScoreClick = useCallback(
    (company, profile, _profileOrder, scoreType) => {
      scoreClickedRef.current = true;
      setScoreDrawerState({
        open: true,
        company,
        clickedProfileId: profile.id,
        scoreType,
      });
    },
    []
  );

  // V2 breakdown drawer: clicking a model score fetches the model config (argo)
  // + scores the company with `explain: true` (cs-api) on demand.
  const [modelDrawerState, setModelDrawerState] = useState<{
    open: boolean;
    company: DataRow | null;
    model: ScoringModel | null;
  }>({ open: false, company: null, model: null });

  const {
    run: runBreakdown,
    result: breakdownResult,
    loading: breakdownLoading,
    error: breakdownError,
  } = useModelScoreBreakdown();

  const handleModelScoreClick: OnModelScoreClick = useCallback(
    (company, model) => {
      scoreClickedRef.current = true;
      setModelDrawerState({ open: true, company, model });
      if (tenantId) {
        void runBreakdown({
          hgId: company.hgId,
          modelId: model._id,
          tenantId,
        });
      }
    },
    [runBreakdown, tenantId]
  );

  // Translate score column field names to backend sort fields.
  // Column fields are "score_{profileId}_{fit|intent}". The backend expects
  // "Fit Score Normalized-{index}" where index is the profile's order in the
  // active profiles array (0-based). This ensures sorting targets the correct
  // profile when multiple profiles are active.
  const rawSortField = tableState.sortModel[0]?.field || DEFAULT_SORT_FIELD;
  const sortDirection = tableState.sortModel[0]?.sort || "desc";
  const sortField = useMemo(() => {
    // V2 model sorts travel via `model_score_sort` (below), never here — so a
    // stale `mscore_*` field (its model removed) falls back to the default.
    if (parseModelSortField(rawSortField)) return DEFAULT_SORT_FIELD;
    if (!rawSortField.startsWith("score_")) return rawSortField;
    if (activeProfiles.length === 0) return DEFAULT_SORT_FIELD;
    const parts = rawSortField.split("_");
    const profileId = parseInt(parts[1], 10);
    const profileIndex = activeProfiles.findIndex((p) => p.id === profileId);
    if (profileIndex < 0) return DEFAULT_SORT_FIELD;
    const suffix = `-${profileIndex}`;
    return rawSortField.endsWith("_intent")
      ? `Intent Score Normalized${suffix}`
      : `Fit Score Normalized${suffix}`;
  }, [rawSortField, activeProfiles]);

  useEffect(() => {
    onActiveSortChange?.(sortField, sortDirection);
  }, [sortField, sortDirection, onActiveSortChange]);

  // V2 sort: sorting by a model's Total/Fit/Need/Intent column goes through
  // the dedicated `model_score_sort` argument (not the generic `sort` array).
  // Only honoured when that model is actually active. `tier` is intentionally
  // not sortable (Total sorts by composite).
  const modelScoreSort = useMemo(() => {
    const parsed = parseModelSortField(rawSortField);
    if (!parsed || !activeModels.some((m) => m.family === parsed.family))
      return undefined;
    return {
      modelId: parsed.family,
      column:
        ModelScoreSortColumn[
          parsed.column.toUpperCase() as keyof typeof ModelScoreSortColumn
        ],
      direction: sortDirection === "asc" ? SortDir.ASC : SortDir.DESC,
    };
  }, [rawSortField, activeModels, sortDirection]);

  // Sort array: when a V2 model sort is active, `model_score_sort` is the
  // primary sort (applied by hermes), so the generic `sort` carries only the
  // stable revenue/hg_id tiebreakers. Otherwise it's the V1 behavior.
  const sortVariable = useMemo(() => {
    if (modelScoreSort) {
      return [
        { field: "revenue_range_min", direction: SortDir.DESC },
        { field: "hg_id", direction: SortDir.ASC },
      ];
    }
    return [
      {
        field: sortField,
        direction: sortDirection === "asc" ? SortDir.ASC : SortDir.DESC,
      },
      // When sorting by a score column, add revenue as secondary sort and
      // hg_id as tertiary sort for stable pagination — matches PIQ behavior.
      ...(rawSortField.startsWith("score_")
        ? [
            { field: "revenue_range_min", direction: SortDir.DESC },
            { field: "hg_id", direction: SortDir.ASC },
          ]
        : []),
    ];
  }, [modelScoreSort, sortField, sortDirection, rawSortField]);

  // Fetch companies data
  const { data, loading, error } = useGetCompanyListBasicQuery({
    variables: {
      filters: effectiveFilters,
      localizeInstalls,
      page: tableState.paginationModel.page,
      per: tableState.paginationModel.pageSize,
      sort: sortVariable,
      scoringProfiles: scoringInput.length > 0 ? scoringInput : undefined,
      // V2: only send when models are active. cs-api defaults model_scores to
      // [] when the arg is absent, so omitting it is safe.
      tenantId: modelScores.length > 0 ? tenantId : undefined,
      modelScores: modelScores.length > 0 ? modelScores : undefined,
      modelScoreSort,
      modelScoreFilter,
    },
    context: { clientName: COMPANY_SEARCH_CLIENT },
  });

  // Extract data from response
  const resultsCount = data?.opportunity_generator_search?.count;
  const companiesData = data?.opportunity_generator_search?.data;

  // Memoize companies to avoid creating new array reference on every render
  const companies = useMemo(() => companiesData || [], [companiesData]);

  // Preserve resultsCount during loading to maintain pagination stability
  const [lastResultsCount, setLastResultsCount] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    if (resultsCount !== undefined && resultsCount !== null) {
      setLastResultsCount(resultsCount);
    }
  }, [resultsCount]);

  useEffect(() => {
    onCountChange?.(
      resultsCount !== undefined && resultsCount !== null
        ? resultsCount
        : undefined
    );
  }, [resultsCount, onCountChange]);

  const count = loading
    ? lastResultsCount
    : resultsCount !== null
      ? resultsCount
      : undefined;

  // Clean data rows (filter nulls)
  const rows: DataRow[] = useMemo(() => {
    return companies.filter(
      (company): company is NonNullable<typeof company> => company !== null
    ) as DataRow[];
  }, [companies]);

  // Columns definition. In the new segments UI, Company Name leads, then score
  // columns, then the remaining firmographics — so the company identity is
  // always visible before its scores. The legacy layout keeps scores first.
  const newSegmentsUi = useFeatureFlag("maNewSegmentsUi", false);
  const { columns, columnGroupingModel } = useMemo(() => {
    const scoring = getScoringColumns(
      activeProfiles,
      rawSortField,
      handleScoreClick
    );
    // V2 model score columns render after the V1 profile columns.
    const modelScoring = getModelScoringColumns(
      activeModels,
      rawSortField,
      handleModelScoreClick
    );
    const scoringColumns = [...scoring.columns, ...modelScoring.columns];
    const scoringGroups = [
      ...scoring.columnGroupingModel,
      ...modelScoring.columnGroupingModel,
    ];
    const baseColumns = getBaseColumns();
    const [nameColumn, ...secondaryColumns] = baseColumns;
    return {
      columns: newSegmentsUi
        ? [nameColumn, ...scoringColumns, ...secondaryColumns]
        : [...scoringColumns, ...baseColumns],
      columnGroupingModel: scoringGroups as GridColumnGroup[],
    };
  }, [
    activeProfiles,
    activeModels,
    rawSortField,
    handleScoreClick,
    handleModelScoreClick,
    newSegmentsUi,
  ]);

  // Company IDs for drawer navigation
  const companyIdsList = useMemo(() => {
    return rows.map((row) => row.hgId);
  }, [rows]);

  // Scores keyed by companyId for drawer navigation
  const scoresMap = useMemo(() => {
    const map: Record<string, DataRow["scores"]> = {};
    for (const row of rows) {
      map[row.hgId] = row.scores;
    }
    return map;
  }, [rows]);

  // Handlers
  const handleRowClick = useCallback((row: DataRow) => {
    if (scoreClickedRef.current) return;
    setDrawerState({
      open: true,
      companyId: row.hgId,
    });
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerState({ open: false, companyId: null });
  }, []);

  // Determine if we should show the count header
  const showEmptyState = !loading && !error && companies.length === 0;
  const showCountHeader = !error && !showEmptyState;

  return (
    <>
      <Box display="flex" flexDirection="column" flex={1} overflow="hidden">
        {/* Count display */}
        {showCountHeader && (
          <Box mb={1} pl={1}>
            {loading ? (
              <Stack direction="row" alignItems="center" sx={{ height: 20 }}>
                <Skeleton
                  variant="text"
                  width={60}
                  height={20}
                  sx={{ display: "inline-block", mr: 1 }}
                />
                <Typography variant="h5">Organizations found</Typography>
              </Stack>
            ) : (
              <Typography variant="h5" sx={{ height: 20 }}>
                {`${formatNumber(count)} Organizations found`}
              </Typography>
            )}
          </Box>
        )}

        {/* DataTable */}
        <Box sx={{ flex: 1, minHeight: 0 }} id="segment-companies-table">
          <DataTable<DataRow>
            rows={rows}
            columns={columns}
            getRowId={(row) => row.hgId}
            sx={{
              "& .score-col-sorted": { bgcolor: "rgba(33, 150, 243, 0.08)" },
              "& .MuiDataGrid-columnHeader.score-col-sorted": {
                bgcolor: "rgba(33, 150, 243, 0.08)",
              },
              // Blue background on group header of the sorted column
              "& .MuiDataGrid-columnHeader--filledGroup.score-group-sorted": {
                bgcolor: "rgba(33, 150, 243, 0.08)",
              },
              // Center the group header content (profile chip)
              "& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainerContent":
                { width: "100%", display: "flex", justifyContent: "center" },
            }}
            rowCount={count ?? 0}
            paginationModel={tableState.paginationModel}
            onPaginationModelChange={tableState.onPaginationModelChange}
            sortModel={tableState.sortModel}
            onSortModelChange={tableState.onSortModelChange}
            loading={loading}
            error={error ?? undefined}
            onRowClick={handleRowClick}
            slots={{
              empty: CompaniesEmptyState,
              error: CompaniesErrorState,
              row: LinkRow,
            }}
            dataGridProps={{
              columnGroupingModel:
                columnGroupingModel.length > 0
                  ? columnGroupingModel
                  : undefined,
            }}
          />
        </Box>
      </Box>

      <CompanyDrawer
        open={drawerState.open}
        onClose={handleDrawerClose}
        companyId={drawerState.companyId}
        companyIds={companyIdsList}
        installFilters={toCompanyProfileInstallFilters(filters)}
        intentFilters={toCompanyProfileIntentFilters(filters)}
        scoresMap={scoresMap}
      />

      <ScoreDrawer
        open={scoreDrawerState.open}
        onClose={() => {
          scoreClickedRef.current = false;
          setScoreDrawerState((s) => ({ ...s, open: false }));
        }}
        companyName={scoreDrawerState.company?.name ?? ""}
        scoresMap={Object.fromEntries(
          activeProfiles.map((p) => [
            p.id,
            getRawProfileScores(scoreDrawerState.company?.scores, p.id),
          ])
        )}
        activeProfiles={activeProfiles}
        clickedProfileId={scoreDrawerState.clickedProfileId}
        scoreType={scoreDrawerState.scoreType}
        onChangeScoring={
          onEditProfile &&
          activeProfiles.length === 1 &&
          scoreDrawerState.clickedProfileId
            ? () => {
                const p = activeProfiles.find(
                  (pr) => pr.id === scoreDrawerState.clickedProfileId
                );
                if (p) onEditProfile(p);
              }
            : undefined
        }
      />

      <ModelScoreDrawer
        open={modelDrawerState.open}
        onClose={() => {
          scoreClickedRef.current = false;
          setModelDrawerState((s) => ({ ...s, open: false }));
        }}
        companyName={modelDrawerState.company?.name ?? ""}
        modelName={modelDrawerState.model?.name ?? ""}
        loading={breakdownLoading}
        error={breakdownError}
        result={breakdownResult}
        onChangeScoring={
          tenantId
            ? () =>
                openAiScoringModel(
                  tenantId,
                  modelDrawerState.model?._id ?? null
                )
            : undefined
        }
      />
    </>
  );
};
